import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';

import store from '@/store';
import { selectReservationData, setDeal } from '@/store/bookingSlice';
import { getEffectiveDealRate, getUnitTypeRate } from '@/journey-based-listing/components/UnitSelect/utils/dealMatching';

export interface DealOption {
    deal: Deal;
    rate: number;
    isActive: boolean;
}

export interface NoDealOption {
    rate: number;
    isActive: boolean;
    show: boolean;
}

export interface UseDealSelectionReturn {
    availableDeals: DealOption[];
    noDealOption: NoDealOption;
    currentDealId: string | null;
    currentDealObject: Deal | null;
    hasDealOptions: boolean;

    selectDeal: (dealId: string) => void;
    clearDeal: () => void;

    getRateForUnitType: (unitType: UnitType, dealId?: string | null) => number;
    getPriceDifference: (unitType: UnitType, dealId?: string | null) => number;
}

export function useDealSelection(
    unitType: UnitType,
    location: Location,
): UseDealSelectionReturn {
    const reservationData = useSelector(selectReservationData);
    const currentDealId = reservationData?.deal ?? null;
    const currentDealObject = reservationData?.dealObject ?? null;

    const availableDeals = useMemo((): DealOption[] => {
        if (!unitType.deals || unitType.deals.length === 0) {
            return [];
        }

        return unitType.deals.map((deal) => ({
            deal,
            rate: getEffectiveDealRate(deal, unitType.rates.rate),
            isActive: currentDealId === deal._id,
        }));
    }, [unitType.deals, unitType.rates.rate, currentDealId]);

    const noDealOption = useMemo((): NoDealOption => {
        const hasDeals = unitType.deals && unitType.deals.length > 0;
        return {
            rate: unitType.rates.rate,
            isActive: !currentDealId,
            show: !(location.rapidstor.hideStandardPrices && hasDeals),
        };
    }, [unitType.rates.rate, unitType.deals, currentDealId, location.rapidstor.hideStandardPrices]);

    const hasDealOptions = availableDeals.length > 0;

    const selectDeal = useCallback((dealId: string) => {
        const deal = unitType.deals?.find((d) => d._id === dealId);
        if (deal) {
            store.dispatch(setDeal({ deal: dealId, dealObject: deal }));
        }
    }, [unitType.deals]);

    const clearDeal = useCallback(() => {
        store.dispatch(setDeal({ deal: null, dealObject: undefined }));
    }, []);

    const getRateForUnitType = useCallback((ut: UnitType, dealId?: string | null): number => (
        getUnitTypeRate(ut, dealId ?? currentDealId ?? undefined)
    ), [currentDealId]);

    const getPriceDifference = useCallback((ut: UnitType, dealId?: string | null): number => {
        const currentRate = currentDealObject
            ? getEffectiveDealRate(currentDealObject, unitType.rates.rate)
            : unitType.rates.rate;
        const targetRate = getUnitTypeRate(ut, dealId ?? currentDealId ?? undefined);
        return targetRate - currentRate;
    }, [currentDealId, currentDealObject, unitType.rates.rate]);

    return {
        availableDeals,
        noDealOption,
        currentDealId,
        currentDealObject,
        hasDealOptions,
        selectDeal,
        clearDeal,
        getRateForUnitType,
        getPriceDifference,
    };
}
